import * as React from "react";
import {
    applyPreset,
    defaultLocale,
    defaultTimeZone,
    type DatePreset,
} from "./FormatDate";

export type WeekdayMode = "short" | "long" | false;

type DateLinePreset =
    | "d.m."
    | "d.m.Y"
    | "d. M."
    | "d. F"
    | "d. M Y"
    | "d. F Y"
    | "M Y"
    | "F Y";

export interface DateLinesOptions {
    locale?: string;
    timeZone?: string;
    weekday?: WeekdayMode; // "short" => "D", "long" => "l", false => omit
    datePreset?: DateLinePreset; // default "d.m.Y"
    container?: keyof JSX.IntrinsicElements;
    lineTag?: keyof JSX.IntrinsicElements;
    className?: string;
    weekdayClassName?: string;
    dateClassName?: string;
    weekdayProps?: React.HTMLAttributes<HTMLElement>;
    dateProps?: React.HTMLAttributes<HTMLElement>;
}

/**
 * Render a two-line (or one-line) localized date block:
 * - Line 1: weekday (optional, short/long)
 * - Line 2: date (token-style preset)
 */
export function renderDateLines(
    date: Date,
    opts: DateLinesOptions = {}
): JSX.Element {
    const {
        locale = defaultLocale,
        timeZone = defaultTimeZone,
        weekday = "short",
        datePreset = "d.m.Y",
        container = "div",
        lineTag = "div",
        className,
        weekdayClassName,
        dateClassName,
        weekdayProps,
        dateProps,
    } = opts;

    const Container = container as any;
    const Line = lineTag as any;

    const weekdayText =
        weekday === "short"
            ? applyPreset(date, "D", locale, timeZone)
            : weekday === "long"
                ? applyPreset(date, "l", locale, timeZone)
                : null;

    const dateText = applyPreset(date, datePreset as DatePreset, locale, timeZone);

    return (
        <Container className={className}>
            {weekdayText && (
                <Line className={weekdayClassName} {...(weekdayProps as any)}>
                    {weekdayText}
                </Line>
            )}
            <Line className={dateClassName} {...(dateProps as any)}>
                {dateText}
            </Line>
        </Container>
    );
}